import { isNonEmpty, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';

import { ForwardCommercialMargin } from '@/App/tiles/CashTile/ForwardTile/ForwardCommercialMargin.tsx';
import { ForwardDoddFranckMidPrice } from '@/App/tiles/CashTile/ForwardTile/ForwardDoddFranckMidPrice.tsx';
import { ForwardFooter } from '@/App/tiles/CashTile/ForwardTile/ForwardFooter.tsx';
import { ForwardRates } from '@/App/tiles/CashTile/ForwardTile/ForwardRates.tsx';
import { NdfFields } from '@/App/tiles/CashTile/ForwardTile/NdfFields.tsx';
import { CashTileHeader } from '@/App/tiles/CashTile/shared/CashTileHeader.tsx';
import { CashTileMainFields } from '@/App/tiles/CashTile/shared/CashTileMainFields.tsx';
import { CreateOrder } from '@/App/tiles/CashTile/shared/CreateOrderFromCash';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type ForwardTileProps = {
  tileId: string;
  isCommercialMarginVisible: boolean;
  onCommercialMarginVisibilityToggle: () => void;
};

export const ForwardTile = (props: ForwardTileProps) => {
  const { tileId, isCommercialMarginVisible, onCommercialMarginVisibilityToggle } = props;
  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));
  const dispatch = useAppDispatch();

  const { currencyPair, isNdf, predealChecks, globalPreDealCheck } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        isNdf: false,
        hasCrossCurrency: false,
        isPricing: false,
        globalPreDealCheck: 'Unknown',
      };
    }

    const predealChecks =
      tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote.predealChecks : undefined;

    const globalPreDealCheck = isNotDefined(predealChecks)
      ? 'Unknown'
      : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
        ? 'Nok'
        : 'Ok';

    return {
      currencyPair: tile?.properties.currencyPair,
      isNdf: tile?.type === 'FxNdf',
      predealChecks,
      globalPreDealCheck,
    };
  }, shallowEqual);

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <CashTileHeader
          tileId={tileId}
          beforeCurrencyPairPicker={
            <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck} />
          }
        />

        {isNonEmpty(currencyPair) && (
          <>
            <ForwardRates tileId={tileId} />
            <ForwardDoddFranckMidPrice tileId={tileId} />
            <CashTileMainFields tileId={tileId} />
            <ForwardCommercialMargin
              tileId={tileId}
              isCommercialMarginVisible={isCommercialMarginVisible}
              onCommercialMarginVisibilityToggle={onCommercialMarginVisibilityToggle}
            />

            {isNdf && <NdfFields tileId={tileId} />}

            <Spacer />

            <ForwardFooter tileId={tileId} />

            <CreateOrder tileId={tileId} />
          </>
        )}
      </VStack>
    </AutoAdvanceForm>
  );
};
